// src/components/Navbar.js

import React, { useState } from 'react'
import {
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Box,
  ListItemIcon,
  Divider,
  Tooltip,
  Typography,
  TextField
} from '@mui/material'

import {
  Logout as LogoutIcon,
  Person as ProfileIcon,
  CreditCard as CreditCardIcon
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

// Çoklu dil desteği
import { useTranslation } from 'react-i18next'

export default function Navbar() {
  const navigate = useNavigate()
  const { currentUser, logout } = useAuth()
  const { t, i18n } = useTranslation()

  const languages = [  
    { code: 'en', label: 'English' },
    { code: 'de', label: 'Deutsch' },
    { code: 'fr', label: 'Français' },
    { code: 'tr', label: 'Türkçe' },
    { code: 'it', label: 'Italiano' },
    { code: 'es', label: 'Español' },
    { code: 'el', label: 'Ελληνικά' },
    { code: 'nl', label: 'Nederlands' },
  ]

  // Profil menüsü için anchor
  const [anchorEl, setAnchorEl] = useState(null)
  const openProfile = Boolean(anchorEl)

 

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleProfileMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = async () => {
    try {
      navigate('/login')
      await logout()
    } catch (error) {
      console.error('Çıkış yapılırken hata:', error)
    }
    handleProfileMenuClose()
  }

  // Dil değiştirme fonksiyonu
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng)
  }

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: '#537494ff',
          color: '#111827',
          boxShadow: '0 2px 10px rgba(15, 23, 42, 0.06)',
          borderBottom: '1px solid rgba(148, 163, 184, 0.25)',
          backdropFilter: 'blur(10px)',
          zIndex: (theme) => theme.zIndex.appBar + 1,
        }}
      >
        <Toolbar
          variant="dense"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            minHeight: { xs: 52, sm: 56 },
            px: { xs: 1.5, sm: 3 },
            gap: 1.5,
          }}
        >

          {/* Orta kısım: Logo */}
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Box
              component="img"
              alt="Community Evoo Logo"
              src="/logo.png"
              sx={{
                width: 120,
                height: 'auto',
                maxWidth: '100%',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/')}
            />
          </Box>


          {/* Sağda: Profil Avatar veya Giriş butonu */}
          <Box sx={{ ml: { xs: 0.5, sm: 2 } }}>
            {currentUser ? (
              <>
                <Tooltip title={t('navbar.profile')}>
                  <IconButton onClick={handleProfileMenuOpen}>
                    <Avatar
                      alt={
                        `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() ||
                        currentUser.email
                      }
                      src={currentUser.avatarUrl || ''}
                      sx={{
                        bgcolor: 'secondary.main',
                        width: { xs: 30, sm: 34 },
                        height: { xs: 30, sm: 34 },
                        fontSize: '0.9rem',
                      }}
                    >
                      {`${currentUser.firstName || ''} ${currentUser.lastName || ''}`
                        .trim()
                        .charAt(0) || currentUser.email.charAt(0)}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={anchorEl}
                  open={openProfile}
                  onClose={handleProfileMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      mt: 1.2,
                      borderRadius: 3,
                      minWidth: 240,
                      boxShadow: '0 12px 30px rgba(15, 23, 42, 0.18)',
                      border: '1px solid rgba(148, 163, 184, 0.35)',
                      overflow: 'hidden',
                    },
                  }}
                >
                  {/* Kullanıcı bilgisi header */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 2,
                      py: 1.75,
                      background:
                        'linear-gradient(135deg, rgba(229, 231, 235, 0.9), rgba(209, 213, 219, 0.9))',
                    }}
                  >
                    <Avatar
                      alt={
                        `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() ||
                        currentUser.email
                      }
                      src={currentUser.avatarUrl || ''}
                      sx={{
                        bgcolor: 'secondary.main',
                        width: 36,
                        height: 36,
                        fontSize: '0.95rem',
                      }}
                    >
                      {`${currentUser.firstName || ''} ${currentUser.lastName || ''}`
                        .trim()
                        .charAt(0) || currentUser.email.charAt(0)}
                    </Avatar>
                    <Box sx={{ overflow: 'hidden' }}>
                      <Typography
                        variant="subtitle2"
                        noWrap
                        sx={{ fontWeight: 600, fontSize: '0.9rem' }}
                      >
                        {`${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() ||
                          currentUser.email}
                      </Typography>
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                      >
                        {t('navbar.profile')}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider />

                  {/* Dil seçimi (dropdown) */}
                  <Box sx={{ px: 2, py: 1.25 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '.08em',
                        color: 'text.secondary',
                        mb: 0.75,
                      }}
                    >
                      {t('navbar.language')}
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={i18n.language}
                      onChange={(e) => {
                        changeLanguage(e.target.value)
                      }}
                      variant="outlined"
                      InputProps={{
                        sx: {
                          borderRadius: 2,
                          fontSize: '0.85rem',
                        },
                      }}
                    >
                      {languages.map((lang) => (
                        <MenuItem key={lang.code} value={lang.code}>
                          {lang.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>

                  <Divider />

                  <MenuItem
                    onClick={() => {
                      navigate('/profile')
                      handleProfileMenuClose()
                    }}
                    sx={{ fontSize: '0.9rem' }}
                  >
                    <ListItemIcon>
                      <ProfileIcon fontSize="small" />
                    </ListItemIcon>
                    {t('navbar.profile')}
                  </MenuItem>

                  <MenuItem
                    onClick={() => {
                      navigate('/plan-billing')
                      handleProfileMenuClose()
                    }}
                    sx={{ fontSize: '0.9rem' }}
                  >
                    <ListItemIcon>
                      <CreditCardIcon fontSize="small" />
                    </ListItemIcon>
                    {t('navbar.planBilling')}
                  </MenuItem>

                  {currentUser?.email === 'serkan@evoosurvey.com' && (
                    <MenuItem
                      onClick={() => {
                        navigate('/customer-admin')
                        handleProfileMenuClose()
                      }}
                      sx={{ fontSize: '0.9rem' }}
                    >
                      <ListItemIcon>
                        <ProfileIcon fontSize="small" />
                      </ListItemIcon>
                      {t('navbar.customerAdmin') || 'Customer Admin'}
                    </MenuItem>
                  )}

                  <MenuItem onClick={handleLogout} sx={{ fontSize: '0.9rem' }}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    {t('navbar.logout')}
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <TextField
                select
                size="small"
                value={i18n.language}
                onChange={(e) => {
                  changeLanguage(e.target.value)
                }}
                variant="outlined"
                sx={{
                  minWidth: 110,                 
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 6,
                    fontSize: '0.75rem',
                    paddingRight: '8px',
                    backgroundColor: '#fff',
                  },
                  '& .MuiSelect-select': {
                    paddingY: 0.5,
                    paddingX: 1.5,
                  },
                }}
              >
                {languages.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>
        </Toolbar>
      </AppBar>

    </>
  )
}
